import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { ShoppingCart, Plus, Minus, Trash2, MapPin, Phone, Mail, User, CreditCard, Search, Flame, AlertCircle, CheckCircle2, Clock, Banknote, CalendarIcon, Tag } from 'lucide-react';
import { AddressChecker, DeliveryZoneInfo } from '@/components/DeliveryInfo';
import useSEO from '@/hooks/useSEO';
import { format, addDays, isBefore, startOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PASTA_TYPES = [
  "Tagliatelle",
  "Penne",
  "Spaghetti"
];

// Service hours configuration
const SERVICE_HOURS = {
  lunch: { start: '12:00', end: '14:30' },
  dinner: { start: '18:00', end: '22:00' }
};

// Days when restaurant is open (0 = Sunday, 3 = Wednesday, etc.)
const OPEN_DAYS = [0, 3, 4, 5, 6]; // Mer, Jeu, Ven, Sam, Dim

const Commander = () => {
  useSEO({
    title: 'Commander en ligne — NotaBene Sympho à Mons',
    description: 'Commandez vos pizzas et plats italiens en ligne chez NotaBene Sympho à Mons. À emporter ou en livraison à Mons et environs. Paiement carte ou espèces.',
    canonical: '/commander',
  });
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [cart, setCart] = useState([]);
  const [activeCategory, setActiveCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [orderType, setOrderType] = useState('emporter');
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [selectedDate, setSelectedDate] = useState(null);
  const [desiredTime, setDesiredTime] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  
  // Pasta selection modal
  const [showPastaModal, setShowPastaModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedPastaType, setSelectedPastaType] = useState('Tagliatelle');
  const [addressCheckResult, setAddressCheckResult] = useState(null);
  const [deliverySettings, setDeliverySettings] = useState(null);
  const [cashSuccess, setCashSuccess] = useState(false);
  // Promo code
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(null); // { code, discount }
  const [promoLoading, setPromoLoading] = useState(false);
  // Supplements (extras for pizzas)
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [selectedSupplements, setSelectedSupplements] = useState([]);
  // Categories considered as "pizzas" (eligible to supplements)
  const PIZZA_CATEGORIES = ['pizza_classique', 'pizza_geante'];
  // Detect the supplements category by its label (case-insensitive, starts with "suppl")
  const isSupplementCategory = (cat) => {
    if (!cat) return false;
    const label = (cat.label || '').toLowerCase();
    return label.startsWith('suppl') || cat.id === 'supplements';
  };
  const supplementCategoryId = (categories.find(isSupplementCategory) || {}).id || null;

  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    notes: '',
  });

  // Generate available time slots based on selected date
  const generateTimeSlots = (date) => {
    if (!date) return [];
    
    const slots = [];
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    
    // Helper to add slots for a service period
    const addSlotsForPeriod = (startTime, endTime) => {
      const [startHour, startMin] = startTime.split(':').map(Number);
      const [endHour, endMin] = endTime.split(':').map(Number);
      
      // Calculate end time minus 30 minutes (can't order 30 min before closing)
      let lastHour = endHour;
      let lastMin = endMin - 30;
      if (lastMin < 0) {
        lastHour -= 1;
        lastMin += 60;
      }
      
      for (let h = startHour; h <= lastHour; h++) {
        for (let m = 0; m < 60; m += 15) {
          if (h === startHour && m < startMin) continue;
          if (h === lastHour && m > lastMin) continue;
          
          // Skip times in the past only if it's today
          if (isToday && (h < currentHour || (h === currentHour && m <= currentMinute + 30))) continue;
          
          const timeStr = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
          slots.push(timeStr);
        }
      }
    };
    
    // Add lunch slots (12:00 - 14:30, last order at 14:00)
    addSlotsForPeriod(SERVICE_HOURS.lunch.start, SERVICE_HOURS.lunch.end);
    
    // Add dinner slots (18:00 - 22:00, last order at 21:30)
    addSlotsForPeriod(SERVICE_HOURS.dinner.start, SERVICE_HOURS.dinner.end);
    
    return slots;
  };

  const availableTimeSlots = generateTimeSlots(selectedDate);
  
  // Check if a date is available for ordering
  const isDateDisabled = (date) => {
    const today = startOfDay(new Date());
    // Can't order in the past
    if (isBefore(date, today)) return true;
    // Can't order more than 14 days in advance
    if (isBefore(addDays(today, 14), date)) return true;
    // Check if restaurant is open on this day
    return !OPEN_DAYS.includes(date.getDay());
  };

  useEffect(() => {
    fetchData();
    fetchDeliverySettings();
  }, []);

  useEffect(() => {
    filterItems();
  }, [menuItems, activeCategory, searchQuery]);

  // Auto-check postal code when 4 digits are entered (debounced)
  useEffect(() => {
    if (orderType !== 'livraison') return;
    const code = (customerInfo.postalCode || '').trim();
    if (code.length !== 4 || !/^\d{4}$/.test(code)) {
      return;
    }
    // Debounce 350 ms so we don't hammer the API while user is still typing
    const t = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/delivery/check-postal-code`, {
          postal_code: code
        });
        setAddressCheckResult(res.data);
      } catch (error) {
        setAddressCheckResult({ in_zone: false, message: 'Erreur de vérification' });
      }
    }, 350);
    return () => clearTimeout(t);
  }, [customerInfo.postalCode, orderType]);

  // Re-validate promo whenever the cart changes (subtotal may drop below min)
  useEffect(() => {
    if (!promoApplied) return;
    const subtotal = cart.reduce((total, item) => {
      const supTotal = (item.supplements || []).reduce((s, sup) => s + (sup.price || 0), 0);
      return total + (item.price + supTotal) * item.quantity;
    }, 0);
    if (subtotal <= 0) {
      setPromoApplied(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post(`${API}/promo-codes/validate`, {
          code: promoApplied.code,
          subtotal,
        });
        if (cancelled) return;
        if (res.data?.valid) {
          setPromoApplied({
            code: res.data.promo.code,
            discount: res.data.discount,
            discount_type: res.data.promo.discount_type,
            discount_value: res.data.promo.discount_value,
          });
        } else {
          setPromoApplied(null);
          toast.info('Code promo retiré (montant du panier modifié)');
        }
      } catch {
        // Silent failure on auto-revalidation
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart]);

  const fetchDeliverySettings = async () => {
    try {
      const response = await axios.get(`${API}/delivery/settings`);
      setDeliverySettings(response.data);
    } catch (error) {
      console.error('Error fetching delivery settings:', error);
    }
  };

  const fetchData = async () => {
    try {
      const [menuRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/menu`),
        axios.get(`${API}/categories`)
      ]);
      setMenuItems(menuRes.data);
      setCategories(categoriesRes.data);
      
      // Set first category as active (skip supplements which is admin-only)
      if (categoriesRes.data.length > 0) {
        const firstVisible = categoriesRes.data.find(c => !isSupplementCategory(c)) || categoriesRes.data[0];
        setActiveCategory(firstVisible.id);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const filterItems = () => {
    let filtered = menuItems.filter(item => item.category === activeCategory);

    if (searchQuery) {
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.description && item.description.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    setFilteredItems(filtered);
  };

  const handleAddToCart = (item) => {
    if (item.has_pasta_choice) {
      setSelectedItem(item);
      setSelectedPastaType('Tagliatelle');
      setShowPastaModal(true);
    } else if (PIZZA_CATEGORIES.includes(item.category) && getSupplements().length > 0) {
      setSelectedItem(item);
      setSelectedSupplements([]);
      setShowCustomizeModal(true);
    } else {
      addToCart(item);
    }
  };

  const confirmPastaSelection = () => {
    if (selectedItem) {
      addToCart(selectedItem, selectedPastaType);
      setShowPastaModal(false);
      setSelectedItem(null);
    }
  };

  const confirmCustomization = () => {
    if (selectedItem) {
      addToCart(selectedItem, null, selectedSupplements);
      setShowCustomizeModal(false);
      setSelectedItem(null);
      setSelectedSupplements([]);
    }
  };

  const toggleSupplement = (sup) => {
    setSelectedSupplements(prev => {
      const exists = prev.find(s => s.id === sup.id);
      if (exists) return prev.filter(s => s.id !== sup.id);
      // Apply +2€ surcharge for giant pizzas (only on paid supplements; free stays free)
      const isGiant = selectedItem?.category === 'pizza_geante';
      const adjustedPrice = sup.price > 0 && isGiant ? sup.price + 2 : sup.price;
      return [...prev, { id: sup.id, name: sup.name, price: adjustedPrice }];
    });
  };

  const getSupplementDisplayPrice = (sup) => {
    const isGiant = selectedItem?.category === 'pizza_geante';
    return sup.price > 0 && isGiant ? sup.price + 2 : sup.price;
  };

  const getSortedSupplements = () => {
    const list = [...getSupplements()];
    // Free supplements first (alpha), then paid (alpha)
    return list.sort((a, b) => {
      const aFree = (a.price || 0) === 0;
      const bFree = (b.price || 0) === 0;
      if (aFree && !bFree) return -1;
      if (!aFree && bFree) return 1;
      return a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' });
    });
  };

  const getSupplements = () => menuItems.filter(
    i => supplementCategoryId && i.category === supplementCategoryId && i.is_available !== false
  );

  const addToCart = (item, pastaType = null, supplements = []) => {
    // Cart key includes supplements signature so different combos remain separate lines
    const supKey = supplements.length
      ? '-sup-' + supplements.map(s => s.id).sort().join('_')
      : '';
    const cartItemId = `${item.id}${pastaType ? `-${pastaType}` : ''}${supKey}`;
    
    setCart(prev => {
      const existing = prev.find(i => i.cartId === cartItemId);
      if (existing) {
        return prev.map(i => 
          i.cartId === cartItemId ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      return [...prev, { 
        ...item, 
        cartId: cartItemId,
        quantity: 1, 
        pasta_type: pastaType,
        supplements: supplements,
      }];
    });
    
    const pastaText = pastaType ? ` (${pastaType})` : '';
    const supText = supplements.length ? ` + ${supplements.length} suppl.` : '';
    toast.success(`${item.name}${pastaText}${supText} ajouté au panier`);
  };

  const updateQuantity = (cartId, delta) => {
    setCart(prev => {
      return prev.map(item => {
        if (item.cartId === cartId) {
          const newQty = item.quantity + delta;
          return newQty > 0 ? { ...item, quantity: newQty } : null;
        }
        return item;
      }).filter(Boolean);
    });
  };

  const removeFromCart = (cartId) => {
    setCart(prev => prev.filter(item => item.cartId !== cartId));
    toast.info('Article retiré du panier');
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => {
      const supTotal = (item.supplements || []).reduce((s, sup) => s + (sup.price || 0), 0);
      return total + (item.price + supTotal) * item.quantity;
    }, 0);
  };

  const getCartSubtotal = () => getCartTotal();
  const getCartFinalTotal = () => {
    const subtotal = getCartTotal();
    const discount = promoApplied?.discount || 0;
    return Math.max(0, subtotal - discount);
  };

  const applyPromoCode = async () => {
    const code = promoCode.trim();
    if (!code) return;
    const subtotal = getCartSubtotal();
    if (subtotal <= 0) {
      toast.error('Ajoute des plats au panier avant');
      return;
    }
    setPromoLoading(true);
    try {
      const res = await axios.post(`${API}/promo-codes/validate`, {
        code,
        subtotal,
      });
      if (res.data?.valid) {
        setPromoApplied({
          code: res.data.promo.code,
          discount: res.data.discount,
          discount_type: res.data.promo.discount_type,
          discount_value: res.data.promo.discount_value,
        });
        toast.success(`Code ${res.data.promo.code} appliqué — économie de ${res.data.discount.toFixed(2)} €`);
      } else {
        setPromoApplied(null);
        toast.error(res.data?.reason || 'Code promo invalide');
      }
    } catch (e) {
      setPromoApplied(null);
      toast.error(e.response?.data?.detail || e.response?.data?.reason || 'Erreur de validation');
    } finally {
      setPromoLoading(false);
    }
  };

  const removePromoCode = () => {
    setPromoApplied(null);
    setPromoCode('');
  };

  const handleSubmitOrder = async () => {
    // Check if online orders are paused
    if (deliverySettings && deliverySettings.orders_enabled === false) {
      toast.error(
        deliverySettings.orders_paused_reason ||
          'Les commandes en ligne sont temporairement indisponibles. Veuillez nous appeler au 065 95 06 49.',
        { duration: 6000 }
      );
      return;
    }

    if (!customerInfo.name || !customerInfo.phone || !customerInfo.email) {
      toast.error('Veuillez remplir votre nom, téléphone et email');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(customerInfo.email)) {
      toast.error('Veuillez entrer une adresse email valide');
      return;
    }

    if (!selectedDate) {
      toast.error('Veuillez sélectionner une date');
      return;
    }

    if (!desiredTime) {
      toast.error('Veuillez sélectionner une heure souhaitée');
      return;
    }

    if (orderType === 'livraison') {
      if (!customerInfo.address) {
        toast.error('Veuillez indiquer votre adresse de livraison');
        return;
      }
      
      // Check if address is in delivery zone
      if (!addressCheckResult || !addressCheckResult.in_zone) {
        toast.error('Votre code postal n\'est pas desservi par notre livraison. Choisissez "À emporter" ou changez d\'adresse.');
        return;
      }
    }

    // Check minimum order for delivery
    if (orderType === 'livraison' && deliverySettings && getCartFinalTotal() < deliverySettings.min_order_amount) {
      toast.error(`Commande minimum de ${deliverySettings.min_order_amount.toFixed(2)} € pour la livraison`);
      return;
    }

    if (cart.length === 0) {
      toast.error('Votre panier est vide');
      return;
    }

    setSubmitting(true);

    try {
      // Format date for backend
      const orderDate = format(selectedDate, 'yyyy-MM-dd');
      
      // Create order
      const orderResponse = await axios.post(`${API}/orders`, {
        items: cart.map(item => ({
          menu_item_id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          is_giant: item.is_giant,
          pasta_type: item.pasta_type || null,
          supplements: (item.supplements || []).map(s => ({ name: s.name, price: s.price })),
        })),
        order_type: orderType,
        payment_method: paymentMethod,
        order_date: orderDate,
        desired_time: desiredTime === 'asap' ? null : desiredTime,
        customer_name: customerInfo.name,
        customer_phone: customerInfo.phone,
        customer_email: customerInfo.email,
        promo_code: promoApplied ? promoApplied.code : null,
        delivery_address: orderType === 'livraison' ? customerInfo.address : null,
        delivery_postal_code: orderType === 'livraison' ? (customerInfo.postalCode || null) : null,
        notes: customerInfo.notes || null,
      });

      // If payment is cash, show success directly
      if (paymentMethod === 'cash') {
        toast.success('Commande envoyée ! Consultez vos emails (et spams) pour la confirmation.', { duration: 6000 });
        // Reset cart and form
        setCart([]);
        setCustomerInfo({ name: '', phone: '', email: '', address: '', notes: '' });
        setSelectedDate(null);
        setDesiredTime('');
        setShowCheckout(false);
        setSubmitting(false);
        setCashSuccess(true);
        return;
      }

      // Create payment session for card
      const paymentResponse = await axios.post(`${API}/payments/create-session`, {
        order_id: orderResponse.data.id,
        origin_url: window.location.origin,
      });

      const checkoutUrl = paymentResponse.data?.url;
      if (!checkoutUrl || typeof checkoutUrl !== 'string' || !checkoutUrl.startsWith('http')) {
        toast.error(
          "Impossible d'initier le paiement par carte. Veuillez réessayer ou choisir le paiement en espèces.",
          { duration: 6000 }
        );
        setSubmitting(false);
        return;
      }

      // Redirect to Stripe Checkout
      toast.info('Redirection vers le paiement sécurisé...', { duration: 3000 });
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Error creating order:', error);
      const errMsg = error?.response?.data?.detail || "Erreur lors de la commande. Veuillez réessayer.";
      toast.error(errMsg, { duration: 6000 });
      setSubmitting(false);
    }
  };

  // Get category count for display
  const getCategoryCount = (categoryId) => {
    return menuItems.filter(item => item.category === categoryId).length;
  };

  return (
    <div className="pt-20 min-h-screen bg-[#F5F2EA]" data-testid="commander-page">
      {/* Cash success modal */}
      <Dialog open={cashSuccess} onOpenChange={setCashSuccess}>
        <DialogContent className="max-w-md" data-testid="cash-success-dialog">
          <DialogHeader>
            <div className="w-16 h-16 rounded-full bg-[#3A6351]/10 flex items-center justify-center mx-auto mb-3">
              <CheckCircle2 className="w-8 h-8 text-[#3A6351]" />
            </div>
            <DialogTitle className="text-center font-['Playfair_Display'] text-2xl">
              Commande confirmée !
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-[#57534E] text-center">
              Merci pour votre commande ! Nous préparons vos plats avec soin.
            </p>
            <div className="bg-[#FEF3C7] border border-[#F59E0B]/30 rounded-xl p-4">
              <p className="text-[#78350F] text-sm font-semibold mb-1">
                📧 Vérifiez vos emails (et vos spams)
              </p>
              <p className="text-[#78350F] text-sm">
                Un email de confirmation vous a été envoyé avec le récapitulatif. Pensez à le consulter <strong>absolument</strong> pour valider l'heure de votre commande.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setCashSuccess(false)}
              className="bg-[#C62E2E] hover:bg-[#a02424] text-white rounded-full w-full"
              data-testid="cash-success-close"
            >
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Orders paused banner */}
      {deliverySettings && deliverySettings.orders_enabled === false && (
        <div className="bg-amber-500 text-white py-3 px-4" data-testid="orders-paused-customer-banner">
          <div className="max-w-7xl mx-auto flex items-start gap-3 text-sm">
            <span className="text-lg">⏸️</span>
            <div className="flex-1">
              <strong>Commandes en ligne temporairement indisponibles.</strong>
              {deliverySettings.orders_paused_reason && (
                <span className="block opacity-95 mt-0.5">{deliverySettings.orders_paused_reason}</span>
              )}
              <span className="block opacity-90 mt-1">
                Vous pouvez nous appeler au <a href="tel:+3265950649" className="underline font-semibold">065 95 06 49</a>.
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <section className="bg-white py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#C62E2E] font-medium tracking-widest uppercase mb-4">
            Commander en ligne
          </p>
          <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold text-[#1C1917] mb-4">
            {showCheckout ? 'Finaliser la commande' : 'Choisissez vos plats'}
          </h1>
          <p className="text-[#57534E] text-lg">
            {showCheckout 
              ? 'Vérifiez votre commande et procédez au paiement'
              : 'À emporter ou en livraison, régalez-vous !'}
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!showCheckout ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Menu Section */}
            <div className="lg:col-span-2">
              {/* Category Tabs & Search */}
              <div className="bg-white rounded-2xl p-4 mb-6 shadow-sm">
                <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                  <Tabs value={activeCategory} onValueChange={setActiveCategory}>
                    <TabsList className="flex flex-wrap gap-2 bg-transparent h-auto p-0">
                      {categories.filter(c => !isSupplementCategory(c)).map(cat => (
                        <TabsTrigger
                          key={cat.id}
                          value={cat.id}
                          className="data-[state=active]:bg-[#C62E2E] data-[state=active]:text-white rounded-full px-4 py-2 text-sm"
                          data-testid={`order-filter-${cat.id}`}
                        >
                          {cat.icon && <span className="mr-1">{cat.icon}</span>}
                          {cat.label}
                          <span className="ml-1 opacity-70">({getCategoryCount(cat.id)})</span>
                        </TabsTrigger>
                      ))}
                    </TabsList>
                  </Tabs>

                  <div className="relative w-full md:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#57534E]" />
                    <Input
                      placeholder="Rechercher..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 rounded-full border-stone-200"
                      data-testid="order-search"
                    />
                  </div>
                </div>
              </div>

              {/* Menu Items */}
              <div className="space-y-3">
                {loading ? (
                  <div className="flex justify-center py-20">
                    <div className="spinner"></div>
                  </div>
                ) : filteredItems.length === 0 ? (
                  <div className="bg-white rounded-xl p-8 text-center">
                    <p className="text-[#57534E]">Aucun plat trouvé</p>
                  </div>
                ) : (
                  filteredItems.map((item) => {
                    const isUnavailable = item.is_available === false;
                    
                    return (
                      <div
                        key={item.id}
                        className={`bg-white rounded-xl p-4 shadow-sm transition-all flex items-center justify-between gap-4 ${
                          isUnavailable ? 'opacity-50 bg-gray-50' : 'hover:shadow-md'
                        }`}
                        data-testid={`order-item-${item.id}`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className={`font-semibold ${isUnavailable ? 'text-gray-500 line-through' : 'text-[#1C1917]'}`}>
                              {item.name}
                            </h3>
                            {item.is_giant && (
                              <span className="bg-[#C62E2E] text-white text-xs px-2 py-0.5 rounded font-semibold flex items-center gap-1">
                                <Flame className="w-3 h-3" /> GÉANTE
                              </span>
                            )}
                            {item.has_pasta_choice && !isUnavailable && (
                              <span className="bg-[#F59E0B] text-white text-xs px-2 py-0.5 rounded font-semibold">
                                Choix pâtes
                              </span>
                            )}
                            {isUnavailable && (
                              <span className="bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded font-semibold">
                                Indisponible
                              </span>
                            )}
                          </div>
                          {item.description && (
                            <p className="text-[#57534E] text-sm mt-1">{item.description}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-4">
                          <span className={`price-tag text-xl font-bold ${isUnavailable ? 'text-gray-400' : ''}`}>
                            {item.price.toFixed(2).replace('.', ',')} €
                          </span>
                          {isUnavailable ? (
                            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                              <AlertCircle className="w-5 h-5 text-gray-400" />
                            </div>
                          ) : (
                            <Button
                              onClick={() => handleAddToCart(item)}
                              className="bg-[#C62E2E] hover:bg-[#a02424] text-white rounded-full w-10 h-10 p-0"
                              data-testid={`add-to-cart-${item.id}`}
                            >
                              <Plus className="w-5 h-5" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Cart Section */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl p-6 shadow-lg sticky top-32">
                <div className="flex items-center gap-3 mb-6">
                  <ShoppingCart className="w-6 h-6 text-[#C62E2E]" />
                  <h2 className="font-['Playfair_Display'] text-xl font-semibold">
                    Votre panier
                  </h2>
                  {cart.length > 0 && (
                    <span className="bg-[#C62E2E] text-white text-xs px-2 py-1 rounded-full">
                      {cart.reduce((sum, item) => sum + item.quantity, 0)}
                    </span>
                  )}
                </div>

                {cart.length === 0 ? (
                  <p className="text-[#57534E] text-center py-8">
                    Votre panier est vide
                  </p>
                ) : (
                  <>
                    <div className="space-y-4 max-h-80 overflow-y-auto mb-6">
                      {cart.map((item) => {
                        const supTotal = (item.supplements || []).reduce((s, sup) => s + (sup.price || 0), 0);
                        const linePrice = (item.price + supTotal) * item.quantity;
                        return (
                        <div key={item.cartId} className="flex items-start gap-3" data-testid={`cart-item-${item.cartId}`}>
                          <div className="flex-1">
                            <p className="font-medium text-sm">{item.name}</p>
                            {item.pasta_type && (
                              <p className="text-xs text-[#F59E0B]">{item.pasta_type}</p>
                            )}
                            {(item.supplements || []).length > 0 && (
                              <ul className="mt-0.5">
                                {item.supplements.map((s, idx) => (
                                  <li key={idx} className="text-xs text-[#57534E]">
                                    + {s.name}{' '}
                                    <span className="text-[#3A6351]">
                                      {s.price === 0
                                        ? '(Gratuit)'
                                        : `(+${s.price.toFixed(2).replace('.', ',')} €)`}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            )}
                            <p className="text-[#C62E2E] text-sm font-semibold mt-1">
                              {linePrice.toFixed(2).replace('.', ',')} €
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => updateQuantity(item.cartId, -1)}
                              className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center hover:bg-stone-200"
                              data-testid={`decrease-${item.cartId}`}
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                            <span className="w-8 text-center font-semibold">{item.quantity}</span>
                            <button
                              onClick={() => updateQuantity(item.cartId, 1)}
                              className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center hover:bg-stone-200"
                              data-testid={`increase-${item.cartId}`}
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => removeFromCart(item.cartId)}
                              className="w-8 h-8 rounded-full bg-red-50 text-[#C62E2E] flex items-center justify-center hover:bg-red-100"
                              data-testid={`remove-${item.cartId}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        );
                      })}
                    </div>

                    <div className="border-t border-stone-200 pt-4 mb-6">
                      <div className="flex justify-between items-center text-lg">
                        <span className="font-semibold">Total</span>
                        <span className="price-tag text-2xl font-bold">
                          {getCartFinalTotal().toFixed(2).replace('.', ',')} €
                        </span>
                      </div>
                    </div>

                    <Button
                      onClick={() => setShowCheckout(true)}
                      className="w-full bg-[#C62E2E] hover:bg-[#a02424] text-white rounded-full py-6 text-lg font-semibold"
                      data-testid="proceed-to-checkout"
                    >
                      Passer la commande
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        ) : (
          /* Checkout Form */
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              {/* Order Summary */}
              <div className="mb-8">
                <h3 className="font-['Playfair_Display'] text-xl font-semibold mb-4">
                  Récapitulatif
                </h3>
                <div className="space-y-2">
                  {cart.map((item) => {
                    const supTotal = (item.supplements || []).reduce((s, sup) => s + (sup.price || 0), 0);
                    const linePrice = (item.price + supTotal) * item.quantity;
                    return (
                    <div key={item.cartId} className="text-sm">
                      <div className="flex justify-between">
                        <span>
                          {item.quantity}x {item.name}
                          {item.pasta_type && <span className="text-[#F59E0B]"> ({item.pasta_type})</span>}
                        </span>
                        <span className="font-semibold">{linePrice.toFixed(2)} €</span>
                      </div>
                      {(item.supplements || []).length > 0 && (
                        <ul className="ml-4 mt-0.5 text-xs text-[#57534E]">
                          {item.supplements.map((s, idx) => (
                            <li key={idx}>
                              + {s.name}{' '}
                              {s.price === 0
                                ? '(Gratuit)'
                                : `(+${s.price.toFixed(2).replace('.', ',')} €)`}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    );
                  })}
                </div>
                <div className="border-t border-stone-200 mt-4 pt-4 space-y-2">
                  <div className="flex justify-between text-sm text-[#57534E]">
                    <span>Sous-total</span>
                    <span>{getCartSubtotal().toFixed(2).replace('.', ',')} €</span>
                  </div>

                  {/* Promo code */}
                  {!promoApplied ? (
                    <div className="pt-2">
                      <Label className="text-sm font-semibold flex items-center gap-1 mb-2">
                        <Tag className="w-4 h-4" />
                        Code promo (optionnel)
                      </Label>
                      <div className="flex gap-2">
                        <Input
                          value={promoCode}
                          onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                          placeholder="Entre ton code"
                          className="font-mono uppercase rounded-lg"
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyPromoCode(); } }}
                          data-testid="checkout-promo-input"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={applyPromoCode}
                          disabled={promoLoading || !promoCode.trim()}
                          className="rounded-lg shrink-0"
                          data-testid="checkout-promo-apply"
                        >
                          {promoLoading ? '...' : 'Appliquer'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 flex items-center justify-between" data-testid="checkout-promo-applied">
                      <div className="flex items-center gap-2 text-sm">
                        <Tag className="w-4 h-4 text-green-700" />
                        <span className="font-mono font-bold text-green-700">{promoApplied.code}</span>
                        <span className="text-green-800">−{promoApplied.discount.toFixed(2).replace('.', ',')} €</span>
                      </div>
                      <button
                        type="button"
                        onClick={removePromoCode}
                        className="text-xs text-green-700 hover:underline"
                        data-testid="checkout-promo-remove"
                      >
                        Retirer
                      </button>
                    </div>
                  )}

                  <div className="flex justify-between items-baseline pt-2 border-t border-stone-100">
                    <span className="font-semibold text-lg">Total</span>
                    <span className="price-tag text-2xl font-bold">
                      {getCartFinalTotal().toFixed(2).replace('.', ',')} €
                    </span>
                  </div>
                </div>
              </div>

              {/* Order Type */}
              <div className="mb-6">
                <Label className="mb-3 block font-semibold">Mode de retrait</Label>
                <RadioGroup value={orderType} onValueChange={setOrderType} className="flex gap-4">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="emporter" id="emporter" data-testid="order-type-emporter" />
                    <Label htmlFor="emporter" className="cursor-pointer">À emporter</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="livraison" id="livraison" data-testid="order-type-livraison" />
                    <Label htmlFor="livraison" className="cursor-pointer">Livraison</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Date Selection */}
              <div className="mb-6">
                <Label className="flex items-center gap-2 mb-2 font-semibold">
                  <CalendarIcon className="w-4 h-4" />
                  Date souhaitée *
                </Label>
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal rounded-lg"
                      data-testid="date-picker-btn"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {selectedDate ? format(selectedDate, 'EEEE d MMMM yyyy', { locale: fr }) : 'Choisir une date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => {
                        setSelectedDate(date);
                        setDesiredTime(''); // Reset time when date changes
                        setCalendarOpen(false);
                      }}
                      disabled={isDateDisabled}
                      locale={fr}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <p className="text-xs text-[#57534E] mt-1">
                  Ouvert : Mercredi au Dimanche (jusqu'à 14 jours à l'avance)
                </p>
              </div>

              {/* Desired Time */}
              <div className="mb-6">
                <Label htmlFor="desired-time" className="flex items-center gap-2 mb-2 font-semibold">
                  <Clock className="w-4 h-4" />
                  Heure souhaitée *
                </Label>
                {/* ASAP option — only available if the order is for today */}
                {selectedDate && (() => {
                  const today = new Date();
                  const sameDay =
                    selectedDate.getFullYear() === today.getFullYear() &&
                    selectedDate.getMonth() === today.getMonth() &&
                    selectedDate.getDate() === today.getDate();
                  if (!sameDay) return null;
                  return (
                    <label
                      htmlFor="asap-checkbox"
                      className={`mb-2 flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        desiredTime === 'asap'
                          ? 'border-[#C62E2E] bg-red-50'
                          : 'border-stone-200 hover:bg-stone-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        id="asap-checkbox"
                        checked={desiredTime === 'asap'}
                        onChange={(e) => setDesiredTime(e.target.checked ? 'asap' : '')}
                        className="w-4 h-4 accent-[#C62E2E]"
                        data-testid="asap-checkbox"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-sm text-[#1C1917]">⚡ Le plus vite possible</p>
                        <p className="text-xs text-[#78716C]">
                          Le restaurant te confirmera l'heure exacte par email.
                        </p>
                      </div>
                    </label>
                  );
                })()}
                <Select
                  value={desiredTime === 'asap' ? '' : desiredTime}
                  onValueChange={setDesiredTime}
                  disabled={!selectedDate || desiredTime === 'asap'}
                >
                  <SelectTrigger className="w-full rounded-lg" data-testid="desired-time-select">
                    <SelectValue placeholder={
                      desiredTime === 'asap'
                        ? 'Au plus vite (l\'heure sera confirmée)'
                        : selectedDate ? "Choisir une heure" : "Sélectionnez d'abord une date"
                    } />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTimeSlots.length === 0 ? (
                      <SelectItem value="no-slots" disabled>
                        {selectedDate ? "Aucun créneau disponible" : "Sélectionnez une date"}
                      </SelectItem>
                    ) : (
                      availableTimeSlots.map((time) => (
                        <SelectItem key={time} value={time}>
                          {time}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-[#57534E] mt-1">
                  Service: 12h-14h30 et 18h-22h (dernière commande 30 min avant fermeture)
                </p>
              </div>

              {/* Important notice — delay & time confirmation */}
              {deliverySettings && (
                <div
                  className="mb-6 bg-[#FEF3C7] border border-[#F59E0B]/40 rounded-xl p-4 flex gap-3"
                  data-testid="delay-notice"
                >
                  <AlertCircle className="w-5 h-5 text-[#B45309] shrink-0 mt-0.5" />
                  <div className="text-sm text-[#78350F] leading-relaxed">
                    <p className="font-semibold mb-1">⏱️ À propos de votre heure souhaitée</p>
                    <p>
                      Le délai actuel de préparation est d'environ{' '}
                      <strong>
                        {deliverySettings.delivery_time_minutes}
                        {deliverySettings.delivery_time_max_minutes ? `-${deliverySettings.delivery_time_max_minutes}` : ''} minutes
                      </strong>.
                      Votre commande sera confirmée par le restaurant et l'heure souhaitée
                      <strong> n'est pas garantie</strong> selon l'affluence du moment.
                    </p>
                    <p className="mt-1">
                      📧 Vous recevrez un <strong>email de confirmation</strong> avec
                      l'heure exacte validée par le restaurant.
                    </p>
                  </div>
                </div>
              )}

              {/* Payment Method */}
              <div className="mb-8">
                <Label className="mb-3 block font-semibold">Mode de paiement</Label>
                <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="flex gap-4">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="card" id="payment-card" data-testid="payment-card" />
                    <Label htmlFor="payment-card" className="cursor-pointer flex items-center gap-2">
                      <CreditCard className="w-4 h-4" />
                      Carte bancaire
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="cash" id="payment-cash" data-testid="payment-cash" />
                    <Label htmlFor="payment-cash" className="cursor-pointer flex items-center gap-2">
                      <Banknote className="w-4 h-4" />
                      Espèces
                    </Label>
                  </div>
                </RadioGroup>
                {paymentMethod === 'cash' && (
                  <p className="text-xs text-[#F59E0B] mt-2 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    Paiement à effectuer à la {orderType === 'livraison' ? 'réception de la livraison' : 'récupération de votre commande'}
                  </p>
                )}
              </div>

              {/* Customer Info */}
              <div className="space-y-4 mb-8">
                <div>
                  <Label htmlFor="customer-name" className="flex items-center gap-2 mb-2">
                    <User className="w-4 h-4" />
                    Nom complet *
                  </Label>
                  <Input
                    id="customer-name"
                    value={customerInfo.name}
                    onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                    placeholder="Votre nom"
                    className="rounded-lg"
                    data-testid="checkout-name"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="customer-phone" className="flex items-center gap-2 mb-2">
                    <Phone className="w-4 h-4" />
                    Téléphone *
                  </Label>
                  <Input
                    id="customer-phone"
                    type="tel"
                    value={customerInfo.phone}
                    onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                    placeholder="Votre numéro"
                    className="rounded-lg"
                    data-testid="checkout-phone"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="customer-email" className="flex items-center gap-2 mb-2">
                    <Mail className="w-4 h-4" />
                    Email *
                  </Label>
                  <Input
                    id="customer-email"
                    type="email"
                    value={customerInfo.email}
                    onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                    placeholder="Votre email (pour la confirmation)"
                    className="rounded-lg"
                    required
                    data-testid="checkout-email"
                  />
                  <p className="text-xs text-[#78716C] mt-1">
                    📧 La confirmation et le suivi de commande arrivent par email.
                  </p>
                </div>

                {orderType === 'livraison' && (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="postal-code" className="flex items-center gap-2 mb-2">
                        <MapPin className="w-4 h-4" />
                        Code postal *
                      </Label>
                      <Input
                        id="postal-code"
                        value={customerInfo.postalCode || ''}
                        onChange={(e) => {
                          // Keep digits only, max 4
                          const v = e.target.value.replace(/\D/g, '').slice(0, 4);
                          setCustomerInfo({ ...customerInfo, postalCode: v });
                          // Reset previous result; the useEffect below will trigger a fresh auto-check
                          setAddressCheckResult(null);
                        }}
                        placeholder="Ex: 7000"
                        className="rounded-lg w-full"
                        maxLength={4}
                        inputMode="numeric"
                        data-testid="checkout-postal-code"
                        required
                      />
                      {customerInfo.postalCode?.length > 0 && customerInfo.postalCode.length < 4 && (
                        <p className="text-xs text-[#78716C] mt-1">
                          Saisis les 4 chiffres pour vérifier la zone de livraison.
                        </p>
                      )}
                      {addressCheckResult && (
                        <div className={`mt-2 p-3 rounded-lg flex items-center gap-2 ${
                          addressCheckResult.in_zone 
                            ? 'bg-green-50 text-green-700' 
                            : 'bg-red-50 text-red-700'
                        }`} data-testid="postal-code-result">
                          {addressCheckResult.in_zone ? (
                            <CheckCircle2 className="w-5 h-5" />
                          ) : (
                            <AlertCircle className="w-5 h-5" />
                          )}
                          <span>{addressCheckResult.message}</span>
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <Label htmlFor="customer-address" className="flex items-center gap-2 mb-2">
                        <MapPin className="w-4 h-4" />
                        Adresse complète *
                      </Label>
                      <Textarea
                        id="customer-address"
                        value={customerInfo.address}
                        onChange={(e) => setCustomerInfo({ ...customerInfo, address: e.target.value })}
                        placeholder="Rue et numéro, commune"
                        className="rounded-lg"
                        rows={2}
                        data-testid="checkout-address"
                        required
                      />
                    </div>
                    
                    {/* Delivery info */}
                    {deliverySettings && (
                      <div className="bg-[#FAF9F6] rounded-lg p-4 text-sm">
                        <div className="flex items-center gap-2 text-[#57534E]">
                          <Clock className="w-4 h-4 text-[#C62E2E]" />
                          <span>Délai estimé: <strong className="text-[#1C1917]">
                            {deliverySettings.delivery_time_minutes}{deliverySettings.delivery_time_max_minutes ? `-${deliverySettings.delivery_time_max_minutes}` : ''} min
                          </strong></span>
                        </div>
                        {deliverySettings.min_order_amount > 0 && (
                          <div className="flex items-center gap-2 text-[#57534E] mt-2">
                            <AlertCircle className="w-4 h-4 text-[#F59E0B]" />
                            <span>Commande minimum: <strong className="text-[#1C1917]">{deliverySettings.min_order_amount.toFixed(2)} €</strong></span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <Label htmlFor="customer-notes" className="mb-2 block">
                    Remarques (optionnel)
                  </Label>
                  <Textarea
                    id="customer-notes"
                    value={customerInfo.notes}
                    onChange={(e) => setCustomerInfo({ ...customerInfo, notes: e.target.value })}
                    placeholder="Instructions spéciales..."
                    className="rounded-lg"
                    rows={2}
                    data-testid="checkout-notes"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-4">
                <Button
                  variant="outline"
                  onClick={() => setShowCheckout(false)}
                  className="flex-1 rounded-full py-6"
                  data-testid="back-to-menu"
                >
                  Retour au menu
                </Button>
                <Button
                  onClick={handleSubmitOrder}
                  disabled={submitting || availableTimeSlots.length === 0}
                  className="flex-1 bg-[#C62E2E] hover:bg-[#a02424] text-white rounded-full py-6 font-semibold"
                  data-testid="submit-order"
                >
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <div className="spinner w-5 h-5 border-2 border-white/30 border-t-white"></div>
                      Traitement...
                    </span>
                  ) : paymentMethod === 'cash' ? (
                    <span className="flex items-center gap-2">
                      <Banknote className="w-5 h-5" />
                      Confirmer ({getCartFinalTotal().toFixed(2)} €)
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <CreditCard className="w-5 h-5" />
                      Payer {getCartFinalTotal().toFixed(2)} €
                    </span>
                  )}
                </Button>
              </div>
              
              {availableTimeSlots.length === 0 && (
                <div className="mt-4 p-4 bg-red-50 rounded-lg text-center">
                  <p className="text-[#C62E2E] font-medium">
                    Le service est actuellement fermé. Revenez pendant les heures d'ouverture.
                  </p>
                  <p className="text-sm text-[#57534E] mt-1">
                    Service: Mer-Dim 12h-14h30 et 18h-22h
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Pasta Type Selection Modal */}
      <Dialog open={showPastaModal} onOpenChange={setShowPastaModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display'] text-xl">
              Choisissez vos pâtes
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-[#57534E] mb-4">
              Pour <strong>{selectedItem?.name}</strong>, quel type de pâtes souhaitez-vous ?
            </p>
            <Select value={selectedPastaType} onValueChange={setSelectedPastaType}>
              <SelectTrigger className="w-full" data-testid="pasta-type-select">
                <SelectValue placeholder="Choisir un type de pâtes" />
              </SelectTrigger>
              <SelectContent>
                {PASTA_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowPastaModal(false)}
              className="rounded-full"
            >
              Annuler
            </Button>
            <Button
              onClick={confirmPastaSelection}
              className="bg-[#C62E2E] hover:bg-[#a02424] text-white rounded-full"
              data-testid="confirm-pasta-btn"
            >
              Ajouter au panier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Personnaliser (Supplements) Modal — pizzas only */}
      <Dialog open={showCustomizeModal} onOpenChange={setShowCustomizeModal}>
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display'] text-xl">
              Personnaliser votre pizza
            </DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <p className="text-[#57534E] mb-2 text-sm">
              <strong>{selectedItem?.name}</strong> — Ajoutez des suppléments (optionnel) :
            </p>
            {selectedItem?.category === 'pizza_geante' && (
              <div className="bg-[#FEF3C7] border border-[#F59E0B]/40 rounded-lg px-3 py-2 mb-3 text-xs text-[#78350F]">
                ℹ️ Sur les pizzas géantes, chaque supplément payant est <strong>+2 €</strong>.
              </div>
            )}
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {getSortedSupplements().map((sup) => {
                const isChecked = selectedSupplements.some(s => s.id === sup.id);
                const displayPrice = getSupplementDisplayPrice(sup);
                return (
                  <label
                    key={sup.id}
                    htmlFor={`sup-${sup.id}`}
                    className={`flex items-center justify-between gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      isChecked ? 'border-[#C62E2E] bg-red-50' : 'border-stone-200 hover:bg-stone-50'
                    }`}
                    data-testid={`supplement-${sup.id}`}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <input
                        type="checkbox"
                        id={`sup-${sup.id}`}
                        checked={isChecked}
                        onChange={() => toggleSupplement(sup)}
                        className="w-4 h-4 accent-[#C62E2E] cursor-pointer"
                      />
                      <span className="font-medium text-sm text-[#1C1917]">{sup.name}</span>
                    </div>
                    {displayPrice === 0 ? (
                      <span className="text-[#3A6351] font-semibold text-sm whitespace-nowrap">
                        Gratuit
                      </span>
                    ) : (
                      <span className="text-[#3A6351] font-semibold text-sm whitespace-nowrap">
                        +{displayPrice.toFixed(2).replace('.', ',')} €
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
            {selectedSupplements.length > 0 && (
              <div className="mt-4 pt-4 border-t border-stone-200 flex justify-between text-sm">
                <span className="text-[#57534E]">Suppléments ({selectedSupplements.length})</span>
                <span className="font-semibold text-[#C62E2E]">
                  +{selectedSupplements.reduce((s, sup) => s + sup.price, 0).toFixed(2).replace('.', ',')} €
                </span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setShowCustomizeModal(false); setSelectedSupplements([]); }}
              className="rounded-full"
            >
              Annuler
            </Button>
            <Button
              onClick={confirmCustomization}
              className="bg-[#C62E2E] hover:bg-[#a02424] text-white rounded-full"
              data-testid="confirm-customize-btn"
            >
              Ajouter au panier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mobile Floating Cart Bar — visible only on mobile when cart has items and not in checkout */}
      {cart.length > 0 && !showCheckout && (
        <div
          className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-stone-200 shadow-2xl px-4 py-3"
          style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
          data-testid="mobile-cart-bar"
        >
          <Button
            onClick={() => {
              setShowCheckout(true);
              setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
            }}
            className="w-full bg-[#C62E2E] hover:bg-[#a02424] text-white rounded-full py-5 text-base font-semibold flex items-center justify-between px-5"
            data-testid="mobile-checkout-btn"
          >
            <span className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              <span className="bg-white text-[#C62E2E] text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                {cart.reduce((sum, item) => sum + item.quantity, 0)}
              </span>
              Passer la commande
            </span>
            <span className="font-bold">
              {getCartFinalTotal().toFixed(2).replace('.', ',')} €
            </span>
          </Button>
        </div>
      )}
    </div>
  );
};

export default Commander;
